/**
 * Auth Actions - Vuex Store Actions (TypeScript)
 *
 * Migrated from actions.js to use AuthService instead of direct axios
 * All API calls now go through the centralized service layer
 *
 * Key Changes:
 * - Uses AuthService for all API operations
 * - Added TypeScript types
 * - Preserved all business logic (timers, localStorage, routing)
 * - Improved error handling
 */

import { ActionContext } from 'vuex';
import { AuthState } from './mutations';
import { AuthService } from '@/services/AuthService';

type Ctx = ActionContext<AuthState, any>;

// Timer for auto-logout (module-level to have only one timer at a time)
let timer: ReturnType<typeof setTimeout> | null = null;

export default {
  /**
   * User signup (create new account)
   */
  async signup(context: Ctx, payload: { email: string; password: string; name: string; lastName: string }) {
    console.log('This is signup attempt ...');

    try {
      await AuthService.signup({
        email: payload.email,
        password: payload.password,
        name: payload.name,
        lastName: payload.lastName,
      });

      console.log('User created in Cognito user pool.');
    } catch (error: any) {
      console.error('[auth/signup] Error:', error.message);
      throw new Error(error.message || 'Failed to create user');
    }
  },

  /**
   * Confirm user signup with verification code
   */
  async confirmSignUp(context: Ctx, payload: { username: string; code: string }) {
    console.log('confirm sign up');

    try {
      await AuthService.confirmSignUp(payload.username, payload.code);

      console.log('User confirmed in Cognito user pool.');
    } catch (error: any) {
      console.error('[auth/confirmSignUp] Error:', error.message);
      throw new Error(error.message || 'Failed to confirm signup');
    }
  },

  /**
   * Resend confirmation code to user's email
   */
  async resendConfirmationCode(context: Ctx, payload: { username: string }) {
    console.log('resend confirmation code');

    try {
      await AuthService.resendConfirmationCode(payload.username);

      console.log('Confirmation code resent to user.');
    } catch (error: any) {
      console.error('[auth/resendConfirmationCode] Error:', error.message);
      throw new Error(error.message || 'Failed to resend confirmation code');
    }
  },

  /**
   * User login with email and password
   * Sets up token expiration timer and stores user data
   */
  async login(context: Ctx, payload: { email: string; password: string }) {
    console.log('This is login attempt ...');

    try {
      // Call AuthService to authenticate
      const user = await AuthService.login(payload.email, payload.password);

      // Calculate token expiration (1 hour - 10 seconds buffer)
      const expiresIn = 1000 * 60 * 60 - 10000;
      const expirationDate = new Date().getTime() + expiresIn;
      console.log('Expiration date is in', expirationDate);
      console.log('Token expires in ' + expiresIn + 'ms');

      // Store user data in localStorage
      localStorage.setItem('tokenExpiration', String(expirationDate));
      localStorage.setItem('accessToken', user.accessToken);
      localStorage.setItem('idToken', user.idToken);
      localStorage.setItem('refreshToken', user.refreshToken);
      localStorage.setItem('userId', user.userId);
      localStorage.setItem('email', user.email);
      localStorage.setItem('name', user.name);
      localStorage.setItem('lastName', user.lastName);
      localStorage.setItem('role', user.role);

      // Set up auto-logout timer
      timer = setTimeout(() => {
        context.dispatch('autoLogout');
      }, expiresIn);

      // Update Vuex state
      context.commit('setUser', {
        userId: user.userId,
        email: user.email,
        name: user.name,
        lastName: user.lastName,
        role: user.role,
        token: user.accessToken,
      });
    } catch (error: any) {
      console.error('[auth/login] Error:', error.message);
      throw new Error(error.message || 'Failed to login');
    }
  },

  /**
   * Auto-login on app initialization
   * Checks localStorage for existing token and sets up expiration timer
   * No API call - just restores session from localStorage
   */
  autoLogin(context: Ctx) {
    const token = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('userId');
    const email = localStorage.getItem('email');
    const name = localStorage.getItem('name');
    const lastName = localStorage.getItem('lastName');
    const role = localStorage.getItem('role');

    // Check if token has expired
    const tokenExpiration = +(localStorage.getItem('tokenExpiration') || 0);
    const expiresIn = tokenExpiration - new Date().getTime();

    if (expiresIn < 0) {
      console.log('autoLogin: token has been expired - user cannot be logged in');
      return;
    } else {
      console.log('autoLogin: token expires in ' + expiresIn + 'ms');
    }

    // Set up auto-logout timer with remaining time
    timer = setTimeout(() => {
      context.dispatch('autoLogout');
    }, expiresIn);

    // Restore user data in Vuex state
    if (token && userId) {
      context.commit('setUser', {
        userId: userId,
        email: email,
        name: name,
        lastName: lastName,
        role: role,
        token: token,
      });
    }
  },

  /**
   * User logout
   * Calls backend logout endpoint, clears all data, and resets timer
   */
  async logout(context: Ctx) {
    try {
      // Call AuthService to logout from backend
      await AuthService.logout();

      console.log('User logged out from Cognito');
    } catch (error: any) {
      // Log error but continue with local cleanup
      console.error('[auth/logout] Backend logout failed:', error.message);
      // Don't throw - we want to clean up locally even if backend fails
    }

    // Delete FCM token to stop receiving notifications from previous account
    try {
      const { deleteFcmToken } = await import('@/components/firebase/firebase');
      const deleted = await deleteFcmToken();
      if (deleted) {
        console.log('[auth/logout] ✅ FCM token deleted');
      } else {
        console.warn('[auth/logout] ⚠️ FCM token deletion returned false');
      }
    } catch (error: any) {
      console.error('[auth/logout] ❌ Failed to delete FCM token:', error.message);
      // Continue with logout even if FCM deletion fails
    }

    // Clean all Vuex module data
    context.dispatch('device/cleanData', null, { root: true });
    context.dispatch('alarms/cleanData', null, { root: true });
    context.dispatch('catalog/clearCache', null, { root: true });
    context.commit('selection/reset', null, { root: true });
    context.commit('notifications/clearAll', null, { root: true });

    // Clear ALL localStorage to prevent data leakage between accounts
    localStorage.clear();
    
    // Clear session storage as well
    sessionStorage.clear();

    // Clear auto-logout timer
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    // Reset Vuex auth state
    context.commit('setUser', {
      token: null,
      userId: null,
      email: null,
      name: null,
      lastName: null,
      role: null,
    });

    console.log('[auth/logout] ✅ All data cleared, FCM token deleted, user logged out');
  },

  /**
   * Auto-logout triggered by timer expiration
   * Calls logout and ensures redirect to auth page
   */
  async autoLogout(context: Ctx) {
    console.log('[auth/autoLogout] Token expired, logging out...');
    await context.dispatch('logout');
    
    // Force redirect to auth page after a short delay to ensure state is cleared
    setTimeout(() => {
      // Use window.location to force a full page reload and clear any stale state
      if (window.location.pathname !== '/auth') {
        console.log('[auth/autoLogout] Redirecting to /auth');
        window.location.href = '/auth';
      }
    }, 100);
  },

  /**
   * Request password reset (forgot password)
   * Sends verification code to user's email
   */
  async resetPasswordRequest(context: Ctx, payload: { username: string }) {
    console.log('This is reset password request attempt ...');

    try {
      await AuthService.forgotPassword(payload.username);

      console.log('Reset password request sent successfully.');
    } catch (error: any) {
      console.error('[auth/resetPasswordRequest] Error:', error.message);
      throw new Error(error.message || 'Failed to send reset password request');
    }
  },

  /**
   * Confirm password reset with verification code and new password
   */
  async resetPasswordConfirm(context: Ctx, payload: { username: string; code: string; newPassword: string }) {
    console.log('This is reset password confirm attempt ...');

    try {
      await AuthService.confirmForgotPassword({
        username: payload.username,
        code: payload.code,
        newPassword: payload.newPassword,
      });

      console.log('Password reset confirmed successfully.');
    } catch (error: any) {
      console.error('[auth/resetPasswordConfirm] Error:', error.message);
      throw new Error(error.message || 'Failed to confirm password reset');
    }
  },

  /**
   * Delete user account
   * Permanently removes user from Cognito and cleans up all data
   */
  async deleteAccount(context: Ctx) {
    console.log('This is delete account attempt ...');

    const userId = localStorage.getItem('userId');
    if (!userId) {
      throw new Error('No userId found in localStorage');
    }

    try {
      // Call AuthService to delete account from backend
      await AuthService.deleteAccount(userId);

      console.log('User deleted from Cognito user pool.');
    } catch (error: any) {
      console.error('[auth/deleteAccount] Error:', error.message);
      throw new Error(error.message || 'Failed to delete account');
    }

    // Clean all Vuex module data
    context.dispatch('device/cleanData', null, { root: true });
    context.dispatch('alarms/cleanData', null, { root: true });
    context.dispatch('catalog/clearCache', null, { root: true });
    context.commit('selection/reset', null, { root: true });
    context.commit('notifications/clearAll', null, { root: true });

    // Clear ALL localStorage
    localStorage.clear();

    // Clear auto-logout timer
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }

    // Reset Vuex auth state
    context.commit('setUser', {
      token: null,
      userId: null,
      email: null,
      name: null,
      lastName: null,
      role: null,
    });
  },

  /**
   * Refresh access token using refresh token
   * Called automatically by ApiClient on 401 responses
   * Also can be manually triggered
   */
  async refreshAccessToken(context: Ctx) {
    console.log('This is refresh token attempt ...');

    // Clean alarms data (as per original behavior)
    context.dispatch('alarms/cleanData', null, { root: true });

    try {
      // Call AuthService to refresh token
      const newAccessToken = await AuthService.refreshToken();

      if (!newAccessToken) {
        throw new Error('Failed to refresh token');
      }

      console.log('User refreshed token in Cognito user pool.');

      // Set up new auto-logout timer (token expiration handled in AuthService)
      const expiresIn = 1000 * 60 * 60 - 10000;
      const expirationDate = new Date().getTime() + expiresIn;
      console.log('Expiration date is in', expirationDate);
      console.log('Token expires in ' + expiresIn + 'ms');

      // Clear old timer and set new one
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        context.dispatch('autoLogout');
      }, expiresIn);

      // Update Vuex state with refreshed token
      context.commit('setUser', {
        token: localStorage.getItem('accessToken'),
        userId: localStorage.getItem('userId'),
        email: localStorage.getItem('email'),
        name: localStorage.getItem('name'),
        lastName: localStorage.getItem('lastName'),
        role: localStorage.getItem('role'),
      });
    } catch (error: any) {
      console.error('[auth/refreshAccessToken] Error:', error.message);
      // On refresh failure, force logout
      context.dispatch('autoLogout');
      throw new Error(error.message || 'Failed to refresh token');
    }
  },
};
